import React, { useMemo, useState } from "react";
import { useTranslation } from "../hooks/useTranslation";
import { useScrollRevealItem } from "../hooks/useScrollReveal";

const CATEGORY_LABELS = {
  mobile: "Mobile",
  web: "Web",
  game: "Game",
};

const MAX_FEATURES = 3;
const MAX_TECH = 5;
const DESCRIPTION_PREVIEW_CHARS = 155;

const ProjectCard = ({ project, revealIndex = 0 }) => {
  const { t } = useTranslation();
  const { ref: cardRef, isRevealed } = useScrollRevealItem();
  const [expanded, setExpanded] = useState(false);

  const liveLinks = (project.links || []).filter(
    (link) => link.url && link.url !== "#" && link.url.trim() !== ""
  );

  const allFeatures = project.features || [];
  const allTech = project.tech || [];
  const description = project.description || "";

  const visibleFeatures = expanded
    ? allFeatures
    : allFeatures.slice(0, MAX_FEATURES);
  const visibleTech = expanded ? allTech : allTech.slice(0, MAX_TECH);

  const hasMoreContent = useMemo(
    () =>
      description.length > DESCRIPTION_PREVIEW_CHARS ||
      allFeatures.length > MAX_FEATURES ||
      allTech.length > MAX_TECH,
    [description.length, allFeatures.length, allTech.length]
  );

  return (
    <article
      ref={cardRef}
      className={`project-card project-card-v2${
        expanded ? " is-expanded" : ""
      }${isRevealed ? " is-revealed" : ""}`}
      data-category={project.category}
      data-scroll-reveal="card"
      data-scroll-reveal-managed="true"
      style={{ "--reveal-delay": `${0.06 + (revealIndex % 9) * 0.07}s` }}
    >
      <span className="project-card-v2-accent" aria-hidden="true" />

      <div className="project-card-v2-inner">
        <header className="project-card-v2-header">
          <div className="project-icon-wrap">
            <span className="project-icon-shine" aria-hidden="true" />
            <span className="project-icon-ring" aria-hidden="true" />
            <span className="project-icon-glyph" aria-hidden="true">
              {project.icon}
            </span>
          </div>

          <div className="project-card-v2-head">
            <h3>{project.title}</h3>
            <div className="project-meta-row">
              {liveLinks.length > 0 && (
                <span
                  className={`status-badge status-badge-v2 ${project.status.toLowerCase()}`}
                >
                  {project.status}
                </span>
              )}
              <span className="project-cat">
                {CATEGORY_LABELS[project.category] || project.category}
              </span>
            </div>
          </div>
        </header>

        <p className="project-description">{description}</p>

        {visibleFeatures.length > 0 && (
          <ul className="project-highlights" aria-label="Key features">
            {visibleFeatures.map((feature, index) => (
              <li
                key={feature}
                className={
                  expanded && index >= MAX_FEATURES
                    ? "project-reveal-item"
                    : undefined
                }
              >
                {feature}
              </li>
            ))}
          </ul>
        )}

        {visibleTech.length > 0 && (
          <div className="project-tech">
            {visibleTech.map((item, index) => (
              <span
                key={item}
                className={`tech-tag${
                  expanded && index >= MAX_TECH ? " project-reveal-item" : ""
                }`}
              >
                {item}
              </span>
            ))}
          </div>
        )}

        {hasMoreContent && (
          <button
            type="button"
            className="project-see-more"
            onClick={() => setExpanded((prev) => !prev)}
            aria-expanded={expanded}
            aria-label={
              expanded
                ? `${t("projects.seeLess")} — ${project.title}`
                : `${t("projects.seeMore")} — ${project.title}`
            }
          >
            <span>{expanded ? t("projects.seeLess") : t("projects.seeMore")}</span>
            <i
              className={`fas fa-chevron-${expanded ? "up" : "down"}`}
              aria-hidden="true"
            />
          </button>
        )}

        {liveLinks.length > 0 && (
          <footer className="project-links">
            {liveLinks.map((link, linkIndex) => (
              <a
                key={linkIndex}
                href={link.url}
                className={`project-link ${link.type}`}
                target={link.url.startsWith("http") ? "_blank" : "_self"}
                rel={
                  link.url.startsWith("http") ? "noopener noreferrer" : undefined
                }
              >
                <i className={link.icon} aria-hidden="true"></i>
                <span>{link.text}</span>
              </a>
            ))}
          </footer>
        )}
      </div>
    </article>
  );
};

export default ProjectCard;
